import React, { useMemo } from 'react';
import { alpha, Box, Chip, Collapse, IconButton, Stack, Theme, Typography, useTheme } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { useBlendColors } from 'hooks/useDesign';

type CardColor = 'warning' | 'primary';

interface IssueCardProps {
    color: CardColor;
    icon?: string;
    title: string;
    badge: React.ReactNode;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}

export function IssueCardRows({ children }: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                borderRadius: '3px',
                border: '1px solid',
                borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                backgroundColor: 'background.default',
                overflow: 'hidden',
            }}
        >
            {children}
        </Box>
    );
}

export default function IssueCard({
    color,
    icon = 'info-circle',
    title,
    badge,
    expanded,
    onToggle,
    children,
}: IssueCardProps) {
    const { t } = useBundleTranslation('components/common/issue-card');
    const appTheme = useTheme();

    const colorVal = useMemo(() => {
        switch (color) {
            case 'primary':
                return appTheme.palette.primary.main;
            case 'warning':
                return appTheme.palette.warning.main;
        }
    }, [appTheme, color]);

    return (
        <Box
            sx={{
                border: '1px solid',
                borderLeft: `3px solid`,
                borderRadius: '3px',
                padding: '12px',
                backgroundColor: useBlendColors(alpha(colorVal, 0.08)),
                borderColor: useBlendColors(alpha(colorVal, 0.24)),
                borderLeftColor: colorVal,
                overflow: 'hidden',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={onToggle}
                sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                        cursor: 'pointer',
                        '.title': {
                            opacity: 0.65,
                        },
                    },
                }}
            >
                <IconMi icon={icon} sx={{ fontSize: '16px', flexShrink: 0 }} />
                <Box sx={{ fontWeight: 600, flexGrow: 1 }} className={'title'}>
                    {title}
                </Box>
                {badge}
                <IconButton
                    size="small"
                    aria-expanded={expanded}
                    aria-label={expanded ? t('collapse') : t('expand')}
                    data-test="issue_card_toggle"
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggle();
                    }}
                    sx={{ p: '6px', flexShrink: 0 }}
                >
                    <IconMi icon={expanded ? 'chevron-up' : 'chevron-down'} sx={{ fontSize: '16px' }} />
                </IconButton>
            </Stack>

            <Collapse in={expanded}>{children}</Collapse>
        </Box>
    );
}
